import * as Sentry from '@sentry/vue';

/**
 * 프린트/PDF에서 레이아웃을 깨뜨리는 인라인 스타일 속성 패턴.
 * - position: fixed/sticky — 프린트 시 요소가 페이지를 벗어남 (absolute는 다이어그램용 허용, #536)
 * - overflow: hidden/auto/scroll — 프린트 시 콘텐츠 잘림 (#528)
 */
const PRINT_UNSAFE_PATTERNS = [
  /\bposition\s*:\s*(fixed|sticky)\b/gi,
  /\boverflow(-[xy])?\s*:\s*(hidden|auto|scroll)\b/gi
];

function stripPrintUnsafeStyles(styleValue) {
  let cleaned = styleValue;
  for (const pattern of PRINT_UNSAFE_PATTERNS) {
    pattern.lastIndex = 0;
    cleaned = cleaned.replace(pattern, '');
  }
  return cleaned
    .replace(/;(\s*;)+/g, ';')
    .replace(/^\s*;\s*/, '')
    .replace(/\s*;\s*$/, '')
    .trim();
}

export default class {
  constructor(option) {
    this.standards = {
      strict: 'strict',
      loose: 'loose',
      html5: 'html5'
    };
    this.selectArray = []; // select 요소를 저장하는 배열
    this.counter = 0;
    this.settings = {
      standard: this.standards.html5,
      extraHead: '', // head 태그에 추가할 요소들, 쉼표로 구분
      extraCss: '', // 추가할 CSS 링크들, 쉼표로 구분
      popTitle: '', // 타이틀 제목
      endCallback: null, // 인쇄 후 호출되는 콜백 함수
      ids: '', // 부분 인쇄를 위한 ID
      selectedLv1PrintOption: []
    };
    Object.assign(this.settings, option);
    this._cleaned = false;
    this._canvasBackups = []; // canvas → img 교체 복원용
    this._bgImageMarks = []; // background-image 표식 attr 복원용 (#684)
    this._bgFailedUrls = new Set(); // 로드 실패한 bg-image URL — 변환 스킵 대상 (#697)

    this.init();
  }

  _cleanup(printAreaClassList) {
    if (this._cleaned) return;
    this._cleaned = true;

    // canvas → img 교체 복원
    for (const { parent, img, canvas } of this._canvasBackups) {
      try {
        if (parent && img.parentNode === parent) {
          parent.replaceChild(canvas, img);
        }
      } catch (e) {
        // 이미 DOM에서 제거된 경우 무시
      }
    }
    this._canvasBackups = [];

    // background-image 표식 attr 복원 (#684)
    for (const node of this._bgImageMarks) {
      try {
        node.removeAttribute('data-print-bg-url');
        node.removeAttribute('data-print-bg-size');
        node.removeAttribute('data-print-bg-position');
      } catch (e) {
        // 무시
      }
    }
    this._bgImageMarks = [];
    this._bgFailedUrls.clear();

    // printArea 클래스 복원
    if (printAreaClassList) {
      if (
        document.querySelector('#full-report-preview-sb') ||
        document.querySelector('#full-report-preview')
      ) {
        printAreaClassList.remove('invisible');
        printAreaClassList.add('d-none');
      }
    }

    // iframe 제거
    try {
      const box = document.getElementById(this.settings.id);
      if (box && box.parentNode) {
        box.parentNode.removeChild(box);
      }
    } catch (e) {
      // 무시
    }

    // endCallback 호출
    if (this.settings.endCallback && typeof this.settings.endCallback === 'function') {
      this.settings.endCallback();
    }
    // 참조 해제
    this.settings.endCallback = null;
    this.elsdom = null;
    this.settings.selectedLv1PrintOption = null;
  }

  // 초기화 함수
  init() {
    const printAreaComponent =
      document.querySelector('#full-report-preview-sb') ||
      document.querySelector('#full-report-preview') ||
      document.querySelector('#assurance-preview');

    if (printAreaComponent && printAreaComponent.parentElement) {
      const printAreaClassList = printAreaComponent.parentElement.classList;

      if (
        document.querySelector('#full-report-preview-sb') ||
        document.querySelector('#full-report-preview')
      ) {
        printAreaClassList.remove('d-none');
        printAreaClassList.add('invisible');
      }

      this.waitForResources(printAreaComponent).then(() => {
        this.counter++;
        this.settings.id = `printArea_${this.counter}`;

        try {
          const PrintAreaWindow = this.getPrintWindow(); // iframe 생성
          this.write(PrintAreaWindow.doc); // 내용 작성
          this.print(PrintAreaWindow, printAreaClassList);
        } catch (error) {
          console.error('[PrintSb] iframe/write/print 에러:', error);
          this._cleanup(printAreaClassList);
        }
      });
    } else {
      console.error('printAreaComponent 또는 그 parentElement를 찾을 수 없습니다.');
      this._cleanup(null);
      return;
    }
  }

  waitForResources(element) {
    return new Promise(resolve => {
      const checkInterval = 500;
      const maxWaitTime = 30000; // 최대 30초
      let elapsedTime = 0;

      // background-image url을 사전 로드해 메인 윈도우 캐시에 적재 (#684)
      // iframe이 same-origin이므로 캐시 공유 → 인쇄 시점 디코드 안정화
      const bgImageLoads = [];
      const bgUrlRegex = /url\((['"]?)([^'")]+)\1\)/g;
      element.querySelectorAll('*').forEach(node => {
        const bg = window.getComputedStyle(node).backgroundImage;
        if (!bg || bg === 'none') return;
        bgUrlRegex.lastIndex = 0;
        let match;
        while ((match = bgUrlRegex.exec(bg)) !== null) {
          const url = match[2];
          if (!url || url.startsWith('data:')) continue;
          bgImageLoads.push(
            new Promise(res => {
              const img = new Image();
              img.onload = res;
              img.onerror = () => {
                // 깨진 URL은 변환 스킵 대상으로 기록 (#697)
                // — 깨진 <img>는 intrinsic size가 없어 100% 폴백이 부모를 못 잡으면
                //   페이지 전체로 확대되는 사례가 있어, 아예 주입하지 않음
                this._bgFailedUrls.add(url);
                res();
              };
              img.src = url;
            })
          );
        }
      });
      const bgAllLoaded = Promise.all(bgImageLoads);

      const intervalId = setInterval(() => {
        const images = Array.from(element.querySelectorAll('img'));
        const allImagesLoaded = images.every(img => img.complete);

        if (allImagesLoaded || elapsedTime >= maxWaitTime) {
          clearInterval(intervalId);
          // background-image까지 디코드 완료 후 1초 안정화 대기
          bgAllLoaded.then(() => setTimeout(resolve, 1000));
        }

        elapsedTime += checkInterval;
      }, checkInterval);
    });
  }

  // 인쇄 함수
  print(PAWindow, printAreaClassList) {
    const paWindow = PAWindow.win;

    const _loaded = () => {
      paWindow.focus();

      // 프린트 시 파일명 설정을 위해 잠시 타이틀 변경
      const originalTitle = document.title;
      if (this.settings.popTitle) {
        document.title = this.settings.popTitle;
      }

      paWindow.print();

      // 타이틀 원복
      if (this.settings.popTitle) {
        document.title = originalTitle;
      }

      // 인쇄 대화상자 닫힌 후 정리
      this._cleanup(printAreaClassList);
    };

    if (window.ActiveXObject) {
      _loaded();
      return false;
    }

    // doc.close() 이후 이미 로드 완료된 경우 즉시 실행
    if (paWindow.document.readyState === 'complete') {
      setTimeout(() => {
        _loaded();
      }, 100);
    } else {
      paWindow.onload = () => {
        _loaded();
      };
    }
  }

  // 문서 작성 함수
  write(PADocument, $ele) {
    PADocument.open();

    // Create html element
    const html = PADocument.createElement('html');
    html.lang = 'ko';

    // Create head
    const head = PADocument.createElement('head');
    head.innerHTML = this.getHead();
    html.appendChild(head);

    // 메인 문서의 CSS 변수(폰트 등)를 iframe에 전달
    const rootStyle = document.documentElement.style;
    const computedStyle = window.getComputedStyle(document.documentElement);
    const reportFont = rootStyle.getPropertyValue('--report-font-family');
    const rootFontSize = rootStyle.fontSize || computedStyle.fontSize;
    if (reportFont) html.style.setProperty('--report-font-family', reportFont);
    if (rootFontSize) html.style.fontSize = rootFontSize;

    // Create body
    const body = PADocument.createElement('body');
    body.innerHTML = this.getBody();
    html.appendChild(body);

    PADocument.appendChild(html);

    // 페이지 번호 업데이트 스크립트 추가
    const script = PADocument.createElement('script');
    script.textContent = `
      (function() {
        var pageNumbers = document.querySelectorAll('.pageNum');
        pageNumbers.forEach(function(el, index) {
          el.textContent = index + 1;
        });
      })();
    `;
    PADocument.body.appendChild(script);

    PADocument.close();
  }

  // DOCTYPE 설정 함수
  docType() {
    if (this.settings.standard === this.standards.html5) {
      return '<!DOCTYPE html>';
    }
    const transitional = this.settings.standard === this.standards.loose ? ' Transitional' : '';
    const dtd = this.settings.standard === this.standards.loose ? 'loose' : 'strict';

    return `<!DOCTYPE HTML PUBLIC "-//W3C//DTD HTML 4.01${transitional}//EN" "http://www.w3.org/TR/html4/${dtd}.dtd">`;
  }

  // head 태그 생성 함수
  getHead() {
    let extraHead = '';
    let links = '';
    let style = '';
    if (this.settings.extraHead) {
      this.settings.extraHead.replace(/([^,]+)/g, m => {
        extraHead += m;
      });
    }

    // 모든 link 태그 복사
    document.querySelectorAll('link[rel="stylesheet"]').forEach(item => {
      if (item.href && item.href.indexOf('.css') >= 0) {
        links += `<link type="text/css" rel="stylesheet" href="${item.href}" >`;
      }
    });

    // 내부 스타일시트만 복사
    const domStyle = document.styleSheets;
    if (domStyle && domStyle.length > 0) {
      for (let i = 0; i < domStyle.length; i++) {
        try {
          // 크로스오리진 스타일시트는 건너뛰기
          if (domStyle[i].href && new URL(domStyle[i].href).origin !== window.location.origin) {
            continue;
          }
          if (domStyle[i].cssRules || domStyle[i].rules) {
            const rules = domStyle[i].cssRules || domStyle[i].rules;
            for (let b = 0; b < rules.length; b++) {
              style += rules[b].cssText;
            }
          }
        } catch (error) {
          Sentry.captureException(new Error('[프린트 canvas 복제] ' + error.message));
        }
      }
    }

    if (this.settings.extraCss) {
      this.settings.extraCss.split(',').forEach(m => {
        links += `<link type="text/css" rel="stylesheet" href="${m.trim()}">`;
      });
    }

    let lv1TopNameCSS = ``;

    const lv1_title_list = Array.isArray(this.settings.selectedLv1PrintOption)
      ? this.settings.selectedLv1PrintOption.map(info => info.name)
      : [];

    for (const lv1_title of lv1_title_list) {
      const sanitizedTitle = lv1_title
        .trim()
        .replace(/[\n\r]+/g, '')
        .replace(/\s+/g, '_');
      lv1TopNameCSS += `
        @page ${sanitizedTitle} {
          @top-right {
            content: " ${lv1_title}";
            font-size: 13px;
            font-family: pretendard;
            color: #333333;
            margin-right: 1cm;
          }

        }
      `;
    }

    // 페이지 번호를 위한 CSS 추가
    const pageNumberCSS = `
    <style type="text/css">
      @media print {
        @page {
          @top-left {
            content: "${this.settings.popTitle}";
            font-size: 13px;
            font-family: Pretendard, sans-serif;
            color: #333333;
            margin-left: 1cm;
          }
          @bottom-right {
            content: counter(page)  ;
            font-size: 13px;
            font-family: pretendard;
            color: #666666;
            margin-right: 1cm;
            margin-left: 1cm;
            border-top: 1px solid #999999;
          }
        }
        @page :first {
          @top-left {
            content: none;
          }
          @top-right {
            content: none;
          }
          @bottom-right {
            content: none;
          }
        }

        @page secondPage {
          @top-left {
            content: none;
          }
          @top-right {
            content: none;
          }
          @bottom-right {
            content: none;
          }
        }

        ${lv1TopNameCSS}

        body {
          counter-reset: page;
        }
      }
    </style>`;

    return `
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>${this.settings.popTitle}</title>
        ${extraHead}
        ${links}
        <style type="text/css">${style}</style>
        ${pageNumberCSS}
      </head>
    `;
  }

  // body 태그 생성 함수
  getBody() {
    let ids = this.settings.ids;
    ids = ids.replace(new RegExp('#', 'g'), '');

    this.elsdom = this.beforeHandler(document.getElementById(ids));
    const ele = this.getFormData(this.elsdom);

    // First Page Content
    let firstPageContent = '';
    const firstPageElement = ele.querySelector('.first-page');
    if (firstPageElement) {
      firstPageContent = firstPageElement.outerHTML;
    }

    // Second Page Content
    let secondPageContent = '';
    const secondPageElement = ele.querySelector('.second-page');
    if (secondPageElement) {
      secondPageElement.style.pageBreakBefore = 'always';
      secondPageElement.style.page = 'secondPage';
      secondPageContent = secondPageElement.outerHTML;
    }

    // Other Elements
    const otherElements = Array.from(ele.children).filter(child => {
      return !child.classList.contains('first-page') && !child.classList.contains('second-page');
    });

    for (const otherElement of otherElements) {
      otherElement.style.page = otherElement.classList[0];
    }

    const htm = otherElements.map(child => child.outerHTML).join('');

    return `
      <body>
        ${firstPageContent}
        ${secondPageContent}
        ${htm}
      </body>
    `;
  }

  // Canvas가 제대로 렌더링되었는지 확인하는 함수들
  isCanvasRendered(canvas) {
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    return imageData.data.some(pixel => pixel !== 0);
  }

  hasValidDimensions(canvas) {
    return (
      canvas.width > 0 && canvas.height > 0 && canvas.offsetWidth > 0 && canvas.offsetHeight > 0
    );
  }

  getRenderedCanvas(canvasList) {
    return Array.from(canvasList).find(canvas => {
      // 1. 크기 확인
      if (!this.hasValidDimensions(canvas)) return false;

      // 2. DOM에서 보이는 상태인지 확인
      if (canvas.offsetParent === null) return false;

      // 3. 실제 렌더링된 내용이 있는지 확인
      try {
        return this.isCanvasRendered(canvas);
      } catch (e) {
        return false;
      }
    });
  }

  // 노드를 복제하기 전에 수행할 작업
  beforeHandler(elsdom) {
    // SVG height="auto" 보정: 인쇄 렌더러가 auto를 지원하지 않아 상하 여백이 생기는 문제 방지
    // viewBox 비율로 높이를 계산하여 제거하면 브라우저가 자동으로 올바른 높이를 적용
    const svgList = elsdom.querySelectorAll('svg[height="auto"]');
    for (const svg of svgList) {
      svg.removeAttribute('height');
    }

    // background-image url을 attr로 표식 (#684)
    // 클론본에서 <img>로 변환하기 위함. computed style은 attached node에서만 유효하므로 원본에서 수집.
    const bgUrlRegex = /url\((['"]?)([^'")]+)\1\)/g;
    elsdom.querySelectorAll('*').forEach(node => {
      const cs = window.getComputedStyle(node);
      const bg = cs.backgroundImage;
      if (!bg || bg === 'none') return;
      bgUrlRegex.lastIndex = 0;
      const match = bgUrlRegex.exec(bg);
      if (!match) return;
      const url = match[2];
      if (!url || url.startsWith('data:')) return;
      node.setAttribute('data-print-bg-url', url);
      node.setAttribute('data-print-bg-size', cs.backgroundSize || 'cover');
      node.setAttribute('data-print-bg-position', cs.backgroundPosition || 'center center');
      this._bgImageMarks.push(node);
    });

    const canvasList = elsdom.querySelectorAll('canvas');
    // canvas를 png 이미지로 변환
    for (let i = 0; i < canvasList.length; i++) {
      try {
        const canvas_id = canvasList[i].id.split('chart_canvas_')[1];

        // 잘 렌더링 된 캔버스 고르기
        const sameIdCanvasList = document.querySelectorAll(
          `canvas[id*="chart_canvas_${canvas_id}"]`
        );
        let selectedCanvas = canvasList[i];

        if (sameIdCanvasList.length > 1) {
          selectedCanvas = this.getRenderedCanvas(sameIdCanvasList) || canvasList[i];
        }

        const _parent = canvasList[i].parentNode;
        const _canvasUrl = selectedCanvas.toDataURL('image/png', 0.7);
        const _img = new Image();
        _img.className = 'canvasImg';
        _img.src = _canvasUrl;

        // 복원용 백업 저장
        this._canvasBackups.push({ parent: _parent, img: _img, canvas: canvasList[i] });

        // 캔버스를 이미지로 교체
        _parent.replaceChild(_img, canvasList[i]);
      } catch (error) {
        console.log(error);
      }
    }

    return elsdom;
  }

  // form 데이터를 처리하는 함수
  getFormData(ele) {
    const copy = ele.cloneNode(true);

    // 프린트 시 레이아웃을 깨뜨리는 인라인 스타일 제거 (#528, #536)
    copy.querySelectorAll('[style]').forEach(node => {
      const raw = node.getAttribute('style');
      const cleaned = stripPrintUnsafeStyles(raw);
      if (cleaned) {
        node.setAttribute('style', cleaned);
      } else {
        node.removeAttribute('style');
      }
    });

    const copiedInputs = copy.querySelectorAll('input,select,textarea');
    const canvasImgList = copy.querySelectorAll('.canvasImg,canvas');
    let selectCount = -1;

    // 모든 <a> 태그의 href 속성을 절대 경로로 변환
    const links = copy.querySelectorAll('a');
    for (let i = 0; i < links.length; i++) {
      const item = links[i];
      const href = item.getAttribute('href');
      if (href) {
        item.setAttribute('href', new URL(href, window.location.href).href);
        item.setAttribute('target', '_blank');
      }
    }

    // 모든 canvas 요소 처리
    for (let i = 0; i < canvasImgList.length; i++) {
      const _parent = canvasImgList[i].parentNode;
      const item = canvasImgList[i];
      if (item.tagName.toLowerCase() === 'canvas') {
        _parent.removeChild(item);
      } else {
        item.style.display = 'inline-block';
        item.style.width = '100%';
      }
    }

    // 모든 입력 요소 처리
    for (let i = 0; i < copiedInputs.length; i++) {
      const item = copiedInputs[i];
      let typeInput = item.getAttribute('type');
      const copiedInput = copiedInputs[i];

      if (!typeInput) {
        typeInput =
          item.tagName === 'SELECT' ? 'select' : item.tagName === 'TEXTAREA' ? 'textarea' : '';
      }

      if (item.tagName === 'INPUT') {
        if (typeInput === 'radio' || typeInput === 'checkbox') {
          if (item.checked) {
            copiedInput.setAttribute('checked', item.checked);
          }
        } else {
          copiedInput.value = item.value;
          copiedInput.setAttribute('value', item.value);
        }
      } else if (typeInput === 'select') {
        selectCount++;
        for (let b = 0; b < ele.querySelectorAll('select').length; b++) {
          const select = ele.querySelectorAll('select')[b];
          !select.getAttribute('newbs') && select.setAttribute('newbs', b);
          if (select.getAttribute('newbs') == selectCount) {
            const opSelectedIndex = ele.querySelectorAll('select')[selectCount].selectedIndex;
            item.options[opSelectedIndex].setAttribute('selected', true);
          }
        }
      } else {
        copiedInput.innerHTML = item.value;
        copiedInput.setAttribute('html', item.value);
      }
    }

    // background-image를 <img>로 변환하여 인쇄 안정성 확보 (#684)
    // 브라우저의 "Print background graphics" 옵션이 OFF여도 출력되도록 보장
    copy.querySelectorAll('[data-print-bg-url]').forEach(node => {
      const url = node.getAttribute('data-print-bg-url');
      const bgSize = node.getAttribute('data-print-bg-size') || 'cover';
      const bgPos = node.getAttribute('data-print-bg-position') || 'center center';
      const objectFit = bgSize === 'cover' ? 'cover' : bgSize === 'contain' ? 'contain' : 'cover';

      // 로드 실패한 URL은 <img>를 주입하지 않고 background-image 인라인만 정리하여 종료 (#697)
      // — 깨진 이미지(X박스)는 intrinsic size가 없어 100%/object-fit 폴백 시 부모를 못 잡으면
      //   페이지 전체로 확대되는 사례가 있음. CSS background-image는 깨지면 아무것도 안 보이므로 동등 처리.
      if (this._bgFailedUrls.has(url)) {
        const cleaned = (node.getAttribute('style') || '')
          .replace(/background-image\s*:[^;]+;?/gi, '')
          .replace(/;{2,}/g, ';')
          .replace(/^;|;$/g, '');
        if (cleaned) {
          node.setAttribute('style', cleaned);
        } else {
          node.removeAttribute('style');
        }
        node.removeAttribute('data-print-bg-url');
        node.removeAttribute('data-print-bg-size');
        node.removeAttribute('data-print-bg-position');
        return;
      }

      // 컨테이너 position 보정: 부재 또는 static이면 relative 강제
      // (static이면 abs 자식이 가장 가까운 positioned 조상까지 escape하여 페이지 전체를 덮을 수 있음, #697)
      // 주의: \bposition은 `background-position`도 매칭(- 가 word boundary) → 분리자 인식 패턴 사용
      let existingStyle = node.getAttribute('style') || '';
      const POSITION_RE = /(?:^|;|\s)position\s*:\s*([a-z-]+)/i;
      const POSITION_STATIC_RE = /(^|;|\s)position\s*:\s*static\b/i;
      const OVERFLOW_RE = /(?:^|;|\s)overflow(-[xy])?\s*:/i;
      const positionMatch = existingStyle.match(POSITION_RE);
      if (!positionMatch) {
        existingStyle = `${existingStyle};position:relative`.replace(/^;/, '');
      } else if (/^static$/i.test(positionMatch[1])) {
        existingStyle = existingStyle.replace(POSITION_STATIC_RE, '$1position:relative');
      }
      // overflow:hidden을 inline !important로 부착하여 abs 이미지가 부모 박스를 넘어 페이지를 덮는 것을 차단 (#697)
      if (!OVERFLOW_RE.test(existingStyle)) {
        existingStyle = `${existingStyle};overflow:hidden !important`.replace(/^;/, '');
      }
      node.setAttribute('style', existingStyle);

      const img = document.createElement('img');
      img.className = 'print-bg-img';
      img.src = url;
      img.alt = '';
      // SVG는 viewBox 기반 intrinsic size로 폴백되어 부모를 넘어 A4 전체를 덮는 사례 발생 (#697)
      // → max-width/max-height로 명시 캡, width/height는 !important로 intrinsic 우선 차단,
      //   inset 단축 대신 top/left 명시(일부 프린트 엔진 호환성)
      img.setAttribute(
        'style',
        [
          'position:absolute',
          'top:0',
          'left:0',
          'width:100% !important',
          'height:100% !important',
          'max-width:100% !important',
          'max-height:100% !important',
          `object-fit:${objectFit}`,
          `object-position:${bgPos}`,
          'z-index:0',
          'pointer-events:none'
        ].join(';')
      );

      // 형제(텍스트 등)가 이미지 위에 오도록 z-index 보정
      Array.from(node.children).forEach(child => {
        const childStyle = child.getAttribute('style') || '';
        let patched = childStyle;
        if (!/\bposition\s*:/i.test(patched)) {
          patched = `${patched};position:relative`.replace(/^;/, '');
        }
        if (!/\bz-index\s*:/i.test(patched)) {
          patched = `${patched};z-index:1`.replace(/^;/, '');
        }
        if (patched !== childStyle) child.setAttribute('style', patched);
      });

      // 이중 출력 방지를 위해 클론본의 background-image 제거
      const cleanedStyle = (node.getAttribute('style') || '')
        .replace(/background-image\s*:[^;]+;?/gi, '')
        .replace(/;{2,}/g, ';')
        .replace(/^;|;$/g, '');
      if (cleanedStyle) {
        node.setAttribute('style', cleanedStyle);
      } else {
        node.removeAttribute('style');
      }

      node.insertBefore(img, node.firstChild);
      node.removeAttribute('data-print-bg-url');
      node.removeAttribute('data-print-bg-size');
      node.removeAttribute('data-print-bg-position');
    });

    return copy;
  }

  // 인쇄 창을 생성하는 함수
  getPrintWindow() {
    const f = this.Iframe();
    return {
      f: f,
      win: f.contentWindow || f,
      doc: f.doc
    };
  }

  // iframe을 생성하는 함수
  Iframe() {
    const frameId = this.settings.id;
    let iframe;
    try {
      iframe = document.createElement('iframe');
      document.body.appendChild(iframe);
      iframe.style.border = '0px';
      iframe.style.position = 'absolute';
      iframe.style.width = '0px';
      iframe.style.height = '0px';
      iframe.style.right = '0px';
      iframe.style.top = '0px';
      iframe.setAttribute('id', frameId);
      iframe.setAttribute('src', 'about:blank');
      iframe.doc = null;
      iframe.doc = iframe.contentDocument
        ? iframe.contentDocument
        : iframe.contentWindow
        ? iframe.contentWindow.document
        : iframe.document;
    } catch (error) {
      Sentry.captureException(new Error('[프린트 iframe 생성] ' + error.message));
      throw new Error(
        '[프린트 iframe 생성] ' + error.message + '. iframes may not be supported in this browser.'
      );
    }

    if (iframe.doc == null) {
      throw new Error('Cannot find document.');
    }

    return iframe;
  }
}
